import { Clock } from 'lucide-react';
import { getChannelIcon } from '@/utils/channels';
import type { ChannelPublicationTime } from '@/services/business/calendar';

// Helper component for rendering regular publication time items
interface RegularPublicationItemProps {
  readonly item: { readonly type: 'regular' | 'ab'; readonly date: Date; readonly time: string; readonly data: readonly ChannelPublicationTime[] };
  readonly isValidated: boolean;
  readonly onOpenSchedule: () => void;
}

export function RegularPublicationItem({ item, isValidated, onOpenSchedule }: Readonly<RegularPublicationItemProps>) {
  const group = item.data;
  const borderClass = isValidated
    ? 'border-warning/30 hover:border-warning/50'
    : 'border-white/10 hover:border-warning/30';
  const textClass = isValidated ? 'text-warning' : 'text-white/60';
  const dateTextClass = isValidated ? 'text-warning/70' : 'text-white/40';
  const iconClass = isValidated ? 'text-warning' : 'text-white/50';

  return (
    <button
      key={`${item.type}-${item.date.toISOString()}-${item.time}`}
      onClick={e => {
        e.stopPropagation();
        onOpenSchedule();
      }}
      className={`glass rounded-lg px-3 py-2 border flex items-center gap-3 transition-colors cursor-pointer ${borderClass}`}
    >
      <div className={`flex items-center gap-2 text-sm ${textClass}`}>
        <Clock className="h-4 w-4" />
        <div className="flex flex-col">
          <span className="tracking-wide">{group[0].time}</span>
          <span className={`text-xs ${dateTextClass}`}>
            {group[0].date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        {group.map(pt => {
          const ChannelIcon = getChannelIcon(pt.channel);
          return (
            <div key={pt.channel} className={iconClass}>
              <ChannelIcon className="h-4 w-4" />
            </div>
          );
        })}
      </div>
    </button>
  );
}

// Helper component for rendering AB testing publication time items
interface AbTestingPublicationItemProps {
  readonly item: { readonly type: 'regular' | 'ab'; readonly date: Date; readonly time: string; readonly data: ChannelPublicationTime };
  readonly isValidated: boolean;
}

export function AbTestingPublicationItem({ item, isValidated }: Readonly<AbTestingPublicationItemProps>) {
  const abPub = item.data;
  const borderClass = isValidated
    ? 'border-violet-500/30 bg-violet-500/5'
    : 'border-white/10';
  const textClass = isValidated ? 'text-violet-400' : 'text-white/60';
  const labelClass = isValidated ? 'text-violet-400' : 'text-white/40';
  const dateTextClass = isValidated ? 'text-violet-400/70' : 'text-white/40';
  const iconClass = isValidated ? 'text-violet-400' : 'text-white/50';
  const YoutubeIcon = getChannelIcon('youtube');

  return (
    <div key="ab-testing" className={`glass rounded-lg px-3 py-2 border flex items-center gap-3 ${borderClass}`}>
      <div className={`flex items-center gap-2 text-sm ${textClass}`}>
        <span className={`text-[10px] font-semibold ${labelClass}`}>A/B</span>
        <div className="flex flex-col">
          <span className="tracking-wide">{abPub.time}</span>
          <span className={`text-xs ${dateTextClass}`}>
            {abPub.date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
          </span>
        </div>
      </div>
      <div className={iconClass}>
        <YoutubeIcon className="h-4 w-4" />
      </div>
    </div>
  );
}

// Helper component for rendering publication time items list
export type PublicationItem = { type: 'regular' | 'ab'; date: Date; time: string; data: any };

interface PublicationTimeItemsProps {
  readonly allItems: PublicationItem[];
  readonly isValidated: boolean;
  readonly onOpenSchedule: () => void;
}

export function PublicationTimeItems({ allItems, isValidated, onOpenSchedule }: Readonly<PublicationTimeItemsProps>) {
  return (
    <>
      {allItems.map((item) => {
        if (item.type === 'regular') {
          return (
            <RegularPublicationItem
              key={`${item.type}-${item.date.toISOString()}-${item.time}`}
              item={item as { type: 'regular' | 'ab'; date: Date; time: string; data: ChannelPublicationTime[] }}
              isValidated={isValidated}
              onOpenSchedule={onOpenSchedule}
            />
          );
        }
        return (
          <AbTestingPublicationItem
            key="ab-testing"
            item={item as { type: 'regular' | 'ab'; date: Date; time: string; data: ChannelPublicationTime }}
            isValidated={isValidated}
          />
        );
      })}
    </>
  );
}
